import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { format } from 'date-fns'
import ModerationActions from './ModerationActions'

interface Report {
  id: string
  target_type: 'thread' | 'comment' | 'post'
  target_id: string
  reporter_id: string | null
  reason: string
  status: 'pending' | 'reviewed' | 'dismissed'
  created_at: string
  reporter: {
    display_name: string
  } | null
}

interface ReportDetailProps {
  report: Report
  onActionComplete: () => void
}

interface ContentData {
  id: string
  title?: string
  body: string
  created_at: string
  author_id: string | null
  author_name: string
  group_id: string
  group_name: string
}

export default function ReportDetail({
  report,
  onActionComplete,
}: ReportDetailProps) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [actionNote, setActionNote] = useState('')

  // Fetch the reported content
  const { data: content, isLoading: isLoadingContent } = useQuery({
    queryKey: ['reported-content', report.target_type, report.target_id],
    queryFn: async () => {
      if (report.target_type === 'thread' || report.target_type === 'post') {
        const { data, error } = await supabase
          .from('threads')
          .select(`
            id,
            title,
            body,
            created_at,
            author_id,
            group_id,
            groups!threads_group_id_fkey (
              name
            ),
            profiles!threads_author_id_fkey (
              display_name
            )
          `)
          .eq('id', report.target_id)
          .single()

        if (error) throw error

        const group = Array.isArray(data.groups) ? data.groups[0] : data.groups
        const profile = Array.isArray(data.profiles) ? data.profiles[0] : data.profiles

        return {
          id: data.id,
          title: data.title,
          body: data.body,
          created_at: data.created_at,
          author_id: data.author_id,
          author_name: profile?.display_name || 'Unknown',
          group_id: data.group_id,
          group_name: group?.name || 'Unknown Group',
        } as ContentData
      } else {
        // Comment
        const { data, error } = await supabase
          .from('comments')
          .select(`
            id,
            body,
            created_at,
            author_id,
            thread_id,
            threads!comments_thread_id_fkey (
              id,
              title,
              group_id,
              groups!threads_group_id_fkey (
                name
              )
            ),
            profiles!comments_author_id_fkey (
              display_name
            )
          `)
          .eq('id', report.target_id)
          .single()

        if (error) throw error

        const thread = Array.isArray(data.threads) ? data.threads[0] : data.threads
        const profile = Array.isArray(data.profiles) ? data.profiles[0] : data.profiles
        const group = Array.isArray(thread?.groups) ? thread?.groups[0] : thread?.groups

        return {
          id: data.id,
          body: data.body,
          created_at: data.created_at,
          author_id: data.author_id,
          author_name: profile?.display_name || 'Unknown',
          group_id: thread?.group_id || '',
          group_name: group?.name || 'Unknown Group',
          title: thread?.title,
        } as ContentData
      }
    },
  })

  // Get all reports for this content
  const { data: allReportsForContent } = useQuery({
    queryKey: ['reports-for-content', report.target_type, report.target_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('reports')
        .select(`
          id,
          reason,
          status,
          created_at,
          profiles!reports_reporter_id_fkey (
            display_name
          )
        `)
        .eq('target_type', report.target_type)
        .eq('target_id', report.target_id)
        .order('created_at', { ascending: false })

      if (error) throw error

      return (data || []).map((r: any) => ({
        ...r,
        reporter: Array.isArray(r.profiles)
          ? (r.profiles[0] || null)
          : r.profiles,
      }))
    },
  })

  const updateReportStatus = useMutation({
    mutationFn: async (status: 'reviewed' | 'dismissed') => {
      if (!user) throw new Error('Not authenticated')

      const { error } = await supabase
        .from('reports')
        .update({ status })
        .eq('id', report.id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] })
      queryClient.invalidateQueries({
        queryKey: ['reports-for-content', report.target_type, report.target_id],
      })
    },
  })

  const handleActionComplete = () => {
    updateReportStatus.mutate('reviewed')
    onActionComplete()
  }

  if (isLoadingContent) {
    return (
      <div
        className="p-6 rounded border-2"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          borderColor: 'var(--bg-tertiary)',
        }}
      >
        <p style={{ color: 'var(--text-primary)' }}>Loading content...</p>
      </div>
    )
  }

  if (!content) {
    return (
      <div
        className="p-6 rounded border-2"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          borderColor: 'var(--bg-tertiary)',
        }}
      >
        <p style={{ color: 'var(--text-primary)' }}>
          Content not found or has been deleted.
        </p>
      </div>
    )
  }

  return (
    <div
      className="p-6 rounded border-2 space-y-6"
      style={{
        backgroundColor: 'var(--bg-secondary)',
        borderColor: 'var(--bg-tertiary)',
      }}
    >
      {/* Report Information */}
      <div>
        <h2
          className="text-2xl font-bold mb-4"
          style={{ color: 'var(--text-primary)' }}
        >
          Report Details
        </h2>
        <div className="space-y-2">
          <div>
            <span
              className="font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              Reason:
            </span>
            <span className="ml-2" style={{ color: 'var(--text-primary)' }}>
              {report.reason}
            </span>
          </div>
          <div>
            <span
              className="font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              Reporter:
            </span>
            <span className="ml-2" style={{ color: 'var(--text-primary)' }}>
              {report.reporter?.display_name || 'Unknown'}
            </span>
          </div>
          <div>
            <span
              className="font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              Reported:
            </span>
            <span className="ml-2" style={{ color: 'var(--text-primary)' }}>
              {format(new Date(report.created_at), 'dd/MM/yyyy, HH:mm:ss')}
            </span>
          </div>
          <div>
            <span
              className="font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              Status:
            </span>
            <span
              className="ml-2 px-2 py-1 text-xs font-semibold rounded"
              style={{
                backgroundColor:
                  report.status === 'pending'
                    ? 'var(--color-error)'
                    : report.status === 'reviewed'
                    ? 'var(--bg-interactive)'
                    : 'var(--bg-tertiary)',
                color: 'var(--text-inverse)',
              }}
            >
              {report.status}
            </span>
          </div>
        </div>
      </div>

      {/* All Reports for This Content */}
      {allReportsForContent && allReportsForContent.length > 1 && (
        <div>
          <h3
            className="text-lg font-bold mb-2"
            style={{ color: 'var(--text-primary)' }}
          >
            All Reports for This Content ({allReportsForContent.length})
          </h3>
          <div className="space-y-2">
            {allReportsForContent.map((r: any) => (
              <div
                key={r.id}
                className="p-3 rounded border-2"
                style={{
                  backgroundColor: 'var(--bg-primary)',
                  borderColor: 'var(--bg-tertiary)',
                }}
              >
                <div className="flex items-center justify-between mb-1">
                  <span
                    className="font-semibold"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {r.reason}
                  </span>
                  <span
                    className="px-2 py-1 text-xs font-semibold rounded"
                    style={{
                      backgroundColor:
                        r.status === 'pending'
                          ? 'var(--color-error)'
                          : r.status === 'reviewed'
                          ? 'var(--bg-interactive)'
                          : 'var(--bg-tertiary)',
                      color: 'var(--text-inverse)',
                    }}
                  >
                    {r.status}
                  </span>
                </div>
                <p
                  className="text-sm"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  By {r.reporter?.display_name || 'Unknown'} •{' '}
                  {format(new Date(r.created_at), 'dd/MM/yyyy, HH:mm')}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Content Preview */}
      <div>
        <h3
          className="text-lg font-bold mb-2"
          style={{ color: 'var(--text-primary)' }}
        >
          Reported Content Preview
        </h3>
        <div
          className="p-4 rounded border-2"
          style={{
            backgroundColor: 'var(--bg-primary)',
            borderColor: 'var(--bg-tertiary)',
          }}
        >
          <div className="mb-2">
            <span
              className="px-2 py-1 text-xs font-semibold rounded"
              style={{
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
              }}
            >
              {report.target_type === 'thread' ? 'Thread' : 'Comment'} in{' '}
              {content.group_name}
            </span>
          </div>
          {content.title && (
            <h4
              className="text-lg font-bold mb-2"
              style={{ color: 'var(--text-primary)' }}
            >
              {content.title}
            </h4>
          )}
          <p className="mb-2" style={{ color: 'var(--text-primary)' }}>
            {content.body}
          </p>
          <p
            className="text-sm"
            style={{ color: 'var(--text-secondary)' }}
          >
            By {content.author_name} •{' '}
            {format(new Date(content.created_at), 'dd/MM/yyyy, HH:mm')}
          </p>
        </div>
      </div>

      {/* Moderation Actions */}
      {report.status === 'pending' && (
        <ModerationActions
          targetType={report.target_type}
          targetId={report.target_id}
          contentAuthorId={content.author_id}
          onActionComplete={handleActionComplete}
        />
      )}

      {/* Dismiss Button */}
      {report.status === 'pending' && (
        <div>
          <button
            onClick={() => {
              if (
                confirm(
                  'Are you sure you want to dismiss this report? This will mark it as dismissed without taking any action.'
                )
              ) {
                updateReportStatus.mutate('dismissed')
              }
            }}
            className="px-4 py-2 font-semibold rounded"
            style={{
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)',
            }}
          >
            Dismiss Report
          </button>
        </div>
      )}
    </div>
  )
}

